import type { BulkActionSourceQueue } from '@truepill/tpos-types'
import { SaveButton } from 'components/ActionButton'
import IconWrapper from 'components/IconWrapper'
import { ButtonsContainer, ModalHeader, ModalWrapper } from 'components/Modal'
import { CancelButton } from 'components/PageStructure'
import Warning from 'components/Warning'
import { useModalContext } from 'providers/Overlays/ModalProvider'
import { useTPCacheContext } from 'providers/TPCacheProvider'
import { Layers } from 'react-feather'
import styled from 'styled-components'
import { bodyPrimaryColor, borderColor, capsuleLightGreyColor } from 'styles/styleVariables'
import type { BulkActionStepInput } from 'types'
import { formatStepLabel, getBulkActionLabel, getBulkActionParamsSummary } from 'utils/bulkActions'

type Props = {
  steps: BulkActionStepInput[]
  orderCount: number
  locationName: string
  sourceQueue?: BulkActionSourceQueue
  onConfirm: () => void
}

const BulkActionConfirmationModal = ({
  steps,
  orderCount,
  locationName,
  sourceQueue,
  onConfirm,
}: Props): JSX.Element => {
  const { dismissModal } = useModalContext()
  const { getLocationNameById } = useTPCacheContext()

  const handleConfirm = () => {
    onConfirm()
    dismissModal()
  }

  const isMulti = steps.length > 1
  const singleStep = !isMulti ? steps[0] : undefined

  return (
    <ModalWrapper id="BulkActionConfirmationModal">
      <ModalHeader>
        <IconWrapper>
          <Layers stroke={bodyPrimaryColor} />
        </IconWrapper>
        <h2>Confirm Bulk Action</h2>
      </ModalHeader>
      <ConfirmationText>
        You are about to perform{' '}
        {isMulti ? (
          <strong>{steps.length} bulk actions</strong>
        ) : (
          <>
            <strong>Bulk {getBulkActionLabel(singleStep!.type)}</strong>
            {singleStep ? (
              <>
                {' '}
                — <strong>{getBulkActionParamsSummary(singleStep, getLocationNameById)}</strong>
              </>
            ) : null}
          </>
        )}{' '}
        for <strong>{orderCount} orders</strong>
        {sourceQueue && (
          <>
            {' '}
            selected from the <strong>{sourceQueue}</strong> queue
          </>
        )}{' '}
        at <strong>{locationName}</strong>.
      </ConfirmationText>
      {isMulti && (
        <StepList>
          {steps.map((step, idx) => {
            const summary = getBulkActionParamsSummary(step, getLocationNameById)
            return (
              <StepRow key={`${step.type}-${idx}`}>
                <StepLabel>{formatStepLabel(idx, step.type)}</StepLabel>
                {summary ? <StepSummary>— {summary}</StepSummary> : null}
              </StepRow>
            )
          })}
        </StepList>
      )}
      <Warning text="This Bulk Action cannot be undone" />
      <ButtonsContainer>
        <CancelButton label="Cancel" onClick={dismissModal} />
        <SaveButton label="Confirm" isModal onClick={handleConfirm} />
      </ButtonsContainer>
    </ModalWrapper>
  )
}

export default BulkActionConfirmationModal

const ConfirmationText = styled.p`
  max-width: 36rem;
  margin: 0.75rem 0;
  font-size: 1rem;
  line-height: 1.5;
`

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0.75rem 0;
  padding: 8px 12px;
  border: 1px solid ${borderColor};
  border-radius: 6px;
`

const StepRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 6px;
  font-size: 0.875rem;
`

const StepLabel = styled.span`
  font-weight: 600;
  flex-shrink: 0;
`

const StepSummary = styled.span`
  color: ${capsuleLightGreyColor};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
